// ./src/pages/private/EventPageEditorPage/blockWidgetForm.ts
//
// ONE block, as the page editor builds it: the form a new block starts from (`emptyBlockForm`) and the widget that form
// saves as (`buildBlockWidgetInput` — its config, its stored widgetType, its title and content).
//
// ⚠ Extracted from `BlockEditDrawer` (ROADMAP A2b #62 P6(d), 2026-09-30) so the Create Event wizard's website step puts
//   blocks on the page EXACTLY as the editor would — a second, hand-written config per block would drift from the
//   editor, and a block the editor cannot open back up is a block the host cannot fix.
// ⚠⚠ THE `widgetType` RULE LIVES HERE NOW: every custom-kind block must reach the `'custom'` branch, or it SAVES and is
//   stored as an IMAGE (`check-block-editor`, which reads this file for that rule and the drawer for `canSave`).
import { STATUSES } from '@sdflc/utils';

import { PUBLIC_ACCESS_RULE, accessRuleInput } from '@/components/features/Events/accessRule';
import type { AccessRuleValue } from '@/components/features/Events/accessRule';

import type {
  PageWidgetInput,
  WidgetConfig,
  WidgetImage,
  TextBlockKind,
  ImageLayout,
  GalleryStyle,
  ImageEffect,
  EmbedAspect,
  WeatherMode,
  WeatherUnits,
  CountdownTarget,
  MapSource,
  GuestLayout,
  GuestAvatarSize,
  WidgetStat,
  DividerStyle,
  HighlightLayout,
  TicketLayout,
  QuoteStyle,
  WidgetCard,
  WidgetLink,
  ButtonLayout,
} from '@/utils/api';

// ⚠ The package's LEAF subpaths, not its index: the index drags every block view (React, Mantine) in, and the Create
//   Event wizard's guard runs this file under Node.
import { TEXT_SOURCE_EVENT_SUMMARY } from '@em/blocks/config';
import type { TextBlockSource } from '@em/blocks/config';
import { blockStyleCaps, widgetTypeForText, widgetTypeForImage } from '@em/blocks/blockKinds';
// ⓘ The subpath, not the barrel: this module is read by Node guards, and the barrel pulls CSS modules in.
import { ORGANIZER_EVENTS_DEFAULT_LIMIT } from '@em/blocks/organizer';
import type { BlockCategory } from '@em/blocks/blockKinds';
import { SHARE_NETWORK_IDS } from '@em/blocks/shareTargets';
import { linkHasTarget, linkKindOf } from '@em/blocks/linkTargets';
import type { BackgroundImageValue } from '@em/blocks/backgroundImage';
import type { ShareNetworkId } from '@em/blocks/shareTargets';

// Cards carry a transient `_id` (never persisted) so drag-and-drop reordering has
// a stable key that survives moves — array index alone is not stable enough.
export type CardDraft = WidgetCard & { _id: string };
export const newCardId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `card-${Math.random().toString(36).slice(2)}`;
export const makeCard = (): CardDraft => ({ _id: newCardId(), title: '', description: '' });

export interface BlockForm {
  category: BlockCategory;
  /** "Who can see this block" (owner 2026-10-03) — widget columns, not config; it can only narrow the page's rule. */
  access: AccessRuleValue;
  textKind: TextBlockKind;
  /** #62 P6(d) — `eventSummary` = the block shows the event's Overview, live (the server fills it). */
  textSource: TextBlockSource | null;
  title: string;
  subtitle: string;
  content: string;
  images: WidgetImage[];
  layout: ImageLayout;
  galleryStyle: GalleryStyle;
  effect: ImageEffect;
  allowFullscreen: boolean;
  autoplay: boolean;
  autoplaySeconds: number;
  embedUrl: string;
  embedHtml: string;
  aspectRatio: EmbedAspect;
  embedHeight: number;
  maxWidth: number | null;
  weatherMode: WeatherMode;
  weatherDays: number;
  weatherUnits: WeatherUnits;
  mapSource: MapSource;
  mapSlotId: string | null;
  mapZoom: number;
  showAddress: boolean;
  showContact: boolean;
  showWebsite: boolean;
  countdownTarget: CountdownTarget;
  countdownDoneText: string;
  scheduleShowVenue: boolean;
  scheduleShowDescription: boolean;
  scheduleShowSpeakers: boolean;
  scheduleShowSpeakerBio: boolean;
  scheduleShowSpeakerWebsite: boolean;
  scheduleShowSpeakerSocial: boolean;
  /** 'all' = every track / room; 'selected' = only `scheduleTracks` (plus sessions with no track). */
  scheduleTrackMode: 'all' | 'selected';
  scheduleTracks: string[];
  /**
   * The people blocks' (guests / hosts) combined filter (owner, 2026-10-21): the roles to include (empty = the block's
   * default — guests, or everyone on the crew), specific people and/or guest groups (either; neither = everyone of those
   * roles), and RSVP statuses (empty = any). Saved as `guestSource: 'filter'`; older blocks are read through `peopleFilterOf`.
   */
  guestRoles: string[];
  guestIds: string[];
  guestGroupIds: string[];
  guestRsvpStatuses: string[];
  /** Show each person's job title and company. */
  guestShowTitle: boolean;
  guestLayout: GuestLayout;
  guestShowRsvp: boolean;
  guestShowBio: boolean;
  guestShowWebsite: boolean;
  guestShowSocial: boolean;
  guestColumns: number;
  guestAvatarSize: GuestAvatarSize;
  guestShowRole: boolean;
  stats: WidgetStat[];
  statsColumns: number;
  videoUrl: string;
  videoUploadedFileId: string | null;
  videoAutoplay: boolean;
  videoMuted: boolean;
  videoLoop: boolean;
  /** Audio block (#72): the music (a stored file's url + id, or a pasted link) and an optional background track. */
  audioUrl: string;
  audioUploadedFileId: string | null;
  audioBackgroundUrl: string;
  audioBackgroundUploadedFileId: string | null;
  audioVolume: number;
  audioBackgroundVolume: number;
  audioAutoplay: boolean;
  audioLoop: boolean;
  /** The name on the player; the visualizer; each track's credit (a library pick fills them — its licence requires it). */
  audioTitle: string;
  audioVisualizer: 'bars' | 'wave' | 'none';
  audioCredit: string;
  audioCreditUrl: string;
  audioCreditLicenseUrl: string;
  audioBackgroundCredit: string;
  audioBackgroundCreditUrl: string;
  audioBackgroundCreditLicenseUrl: string;
  dividerStyle: DividerStyle;
  dividerIcon: string | null;
  dividerHeight: number;
  highlightLayout: HighlightLayout;
  ticketLayout: TicketLayout;
  ticketShowAddons: boolean;
  ticketShowDescription: boolean;
  ticketShowAvailability: boolean;
  ticketShowMembership: boolean;
  quoteText: string;
  quoteAuthor: string;
  quoteDate: string;
  quoteSource: string;
  quoteStyle: QuoteStyle;
  // #55 share block. `shareNetworks` is a LIST, and an empty one is a real choice
  // ("copy/QR only") — distinct from a block that has never been opened, which has no
  // stored key at all and shows every network. See `selectedShareNetworks`.
  shareIntro: string;
  shareMessage: string;
  shareNetworks: ShareNetworkId[];
  shareShowQr: boolean;
  shareShowCopy: boolean;
  contactAudience: 'anyone' | 'recognized';
  contactIntro: string;
  /** #68 — the Lost & found block's text above its buttons. */
  lostFoundIntro: string;
  contactCollectPhone: boolean;
  contactButtonLabel: string;
  contactNotifyCrew: boolean;
  guestbookIntro: string;
  guestbookCanPost: boolean;
  guestbookAudience: 'all' | 'rsvp_status' | 'group';
  guestbookRsvpStatuses: string[];
  guestbookGroupIds: string[];
  // Generic per-entity comment settings — the SAME shape on every comment-supporting block (guest book,
  // POI, accommodation, signup, budget, travel, tasks). Written to config.comments; supersedes the legacy
  // guestbook*/<kind>AllowComments fields (still read as a fallback for older pages).
  commentsAllow: boolean;
  commentsAudience: 'all' | 'rsvp_status' | 'group';
  commentsRsvpStatuses: string[];
  commentsGroupIds: string[];
  // Guest-book only: whether guests may send private direct messages to each other (event-wide DM policy).
  commentsDmMode: 'off' | 'all' | 'groups';
  cards: CardDraft[];
  cardColumns: number;
  poiSource: 'all' | 'category';
  poiCategoryId: string | null;
  poiLayout: 'cards' | 'list';
  poiColumns: number;
  poiShowPrice: boolean;
  poiShowContact: boolean;
  poiShowAddress: boolean;
  poiAllowSignup: boolean;
  poiButtonLabel: string;
  poiUntil: string;
  poiShowOthers: boolean;
  poiAllowUpvote: boolean;
  poiAllowComments: boolean;
  poiAllowPrintBlank: boolean;
  poiAllowPrintMine: boolean;
  accommodationSource: 'all' | 'category';
  accommodationCategoryId: string | null;
  accommodationLayout: 'cards' | 'list';
  accommodationColumns: number;
  accommodationShowPrice: boolean;
  accommodationShowContact: boolean;
  accommodationShowAddress: boolean;
  accommodationShowDistance: boolean;
  accommodationShowBookingCode: boolean;
  accommodationShowRoster: boolean;
  accommodationAllowSignup: boolean;
  accommodationButtonLabel: string;
  accommodationUntil: string;
  accommodationAllowUpvote: boolean;
  accommodationAllowComments: boolean;
  accommodationAllowPrintBlank: boolean;
  accommodationAllowPrintMine: boolean;
  signupSource: 'all' | 'category';
  signupCategoryId: string | null;
  signupLayout: 'cards' | 'list';
  signupColumns: number;
  signupShowPrice: boolean;
  signupShowQuantity: boolean;
  signupShowDescription: boolean;
  signupAllowSignup: boolean;
  signupUntil: string;
  signupButtonLabel: string;
  signupAllowUpvote: boolean;
  signupAllowComments: boolean;
  signupShowOthers: boolean;
  signupCollectPayments: boolean;
  signupAllowPrintBlank: boolean;
  signupAllowPrintMine: boolean;
  pollSource: 'all' | 'category';
  pollCategoryId: string | null;
  pollLayout: 'cards' | 'list';
  pollColumns: number;
  pollShowDescription: boolean;
  pollShowOptions: boolean;
  pollShowCorrect: boolean;
  pollAllowAnswer: boolean;
  pollButtonLabel: string;
  pollUntil: string;
  pollShowResults: boolean;
  /** #147 — results only once the guest has answered. */
  pollResultsAfterAnswer: boolean;
  taskSource: 'all' | 'category';
  taskCategoryId: string | null;
  taskType: 'all' | 'task' | 'appointment';
  taskLayout: 'cards' | 'list';
  taskColumns: number;
  taskShowSchedule: boolean;
  taskShowResponsible: boolean;
  taskShowProgress: boolean;
  taskAllowExecute: boolean;
  travelSource: 'all' | 'selected';
  travelSegmentIds: string[];
  travelShowTravellers: boolean;
  travelShowStatus: boolean;
  travelShowCarrier: boolean;
  travelAllowShare: boolean;
  travelButtonLabel: string;
  travelUntil: string;
  travelShowOthers: boolean;
  budgetSource: 'all' | 'categories';
  budgetAllowContribute: boolean;
  budgetButtonLabel: string;
  budgetUntil: string;
  budgetAllowAddItem: boolean;
  budgetCategoryIds: string[];
  budgetShowPaid: boolean;
  eventMapSource: 'all' | 'category';
  eventMapCategoryId: string | null;
  mapWidgetHeight: number;
  mapShowSchedule: boolean;
  // ⚠ The partner-PACKAGES block keeps its own keys. Sharing `partnerSource` with the partners
  // block would make editing one silently retarget the other on any page that carries both — and a
  // sponsor page usually carries both (what is for sale, and who already bought).
  partnerPackageSource: 'all' | 'kind' | 'selected';
  partnerPackageKind: string | null;
  partnerPackageIds: string[];
  partnerPackageGroupByKind: boolean;
  partnerPackageShowPrice: boolean;
  partnerPackageShowSlots: boolean;
  partnerPackageButtonLabel: string;
  partnerPackageColumns: number;
  volunteerSource: 'all' | 'roles';
  volunteerRoleIds: string[];
  volunteerGroupByRole: boolean;
  volunteerShowRewards: boolean;
  volunteerShowSpots: boolean;
  volunteerButtonLabel: string;
  partnerSource: 'all' | 'kind' | 'package' | 'selected';
  partnerKind: string | null;
  partnerPackageId: string | null;
  partnerIds: string[];
  partnerLayout: 'grid' | 'list';
  partnerColumns: number;
  partnerShowLogo: boolean;
  partnerShowName: boolean;
  partnerShowTier: boolean;
  partnerShowWebsite: boolean;
  partnerShowSocials: boolean;
  artistSource: 'all' | 'billing' | 'picked';
  artistBilling: string | null;
  artistIds: string[];
  artistLayout: 'grid' | 'list';
  artistColumns: number;
  artistShowPhoto: boolean;
  artistShowBlurb: boolean;
  artistShowStage: boolean;
  artistShowSetTime: boolean;
  artistShowLinks: boolean;
  artistShowBilling: boolean;
  /** #165 — on a recurring event: the next date's lineup, or every act. */
  artistDates: 'next' | 'all';
  prizeWinnerSource: 'all' | 'picked';
  prizeDrawIds: string[];
  prizeWinnerShowPrize: boolean;
  prizeWinnerShowPending: boolean;
  prizeWinnerPendingText: string;
  /** ROADMAP B26 — the organizer's events block: all upcoming, or only this event's channel; how many; cards or a list. */
  organizerEventsMode: 'all' | 'channel';
  organizerEventsLimit: number | null;
  organizerEventsLayout: 'cards' | 'list';
  /** ROADMAP B25 — the organizer footer block: which of the profile's public facts to show. */
  organizerFooterShowWebsite: boolean;
  organizerFooterShowSocial: boolean;
  organizerFooterShowEmail: boolean;
  organizerFooterShowPhone: boolean;
  organizerFooterShowAddress: boolean;
  /** #204 — the Fundraising block: one of the event's causes (null = all of them), and what each shows. */
  donateCauseId: string | null;
  donateAllowGifts: boolean;
  donateShowGoal: boolean;
  donateShowDonors: boolean;
  donateShowDonorNames: boolean;
  donateShowDonorAmounts: boolean;
  donateShowDonorComments: boolean;
  gameScoreboardGameId: string;
  gameScoreboardShowTeams: boolean;
  gameScoreboardShowEliminations: boolean;
  gameScoreboardShowPairings: boolean;
  myRoleGameId: string;
  raceResultsSource: 'all' | 'group';
  raceResultsGroupId: string | null;
  raceResultsLimit: number | null;
  raceResultsShowWave: boolean;
  links: WidgetLink[];
  buttonLayout: ButtonLayout;
  backgroundColor: string | null;
  textColor: string | null;
  /** A2e #88 — the block's own background image, over its background colour (premium; the server gates it). */
  backgroundImage: BackgroundImageValue | null;
  columns: number;
  align: 'left' | 'center' | 'right';
  /** A2e #84 — a `BLOCK_FONTS` key (null = the theme's font). */
  fontKey: string | null;
  /** A2e #84 — a `BLOCK_TEXT_SIZES` key (null = the theme's size). */
  textSize: string | null;
  live: boolean;
}

/** What the audio picker hands back (a `MediaSelectorResult`, as much of it as a pick needs). */
export interface AudioPick {
  url: string;
  uploadedFileId?: string | null;
  credit?: { title: string; text: string; url: string | null; licenseUrl?: string | null } | null;
}

/**
 * How one pick changes the audio form (#72; review, 2026-10-01). Pure, so `check-audio-block` RUNS it.
 *  · A pick REPLACES the track's credit — a library track brings its own (its licence requires it), and anything else
 *    clears the previous track's, which would otherwise credit the wrong person under the new sound…
 *  · …EXCEPT the same track confirmed again with no credit (the picker reopened on its URL tab and Save pressed): that
 *    must not wipe the credit a CC BY track needs.
 *  · The player's title follows a library pick when the host has not named it themselves — empty, or still the previous
 *    library track's title (the credit starts with it in quotes); a title the host typed is never replaced.
 */
export const audioPickPatch = (form: BlockForm, which: 'music' | 'background', pick: AudioPick): Partial<BlockForm> => {
  const credit = pick.credit ?? null;
  const currentUrl = (which === 'background' ? form.audioBackgroundUrl : form.audioUrl).trim();
  const sameTrackNoCredit = !credit && currentUrl !== '' && currentUrl === pick.url.trim();

  if (which === 'background') {
    return {
      audioBackgroundUrl: pick.url,
      audioBackgroundUploadedFileId: pick.uploadedFileId ?? null,
      ...(sameTrackNoCredit
        ? {}
        : {
            audioBackgroundCredit: credit?.text ?? '',
            audioBackgroundCreditUrl: credit?.url ?? '',
            audioBackgroundCreditLicenseUrl: credit?.licenseUrl ?? '',
          }),
    };
  }

  const title = form.audioTitle.trim();
  const titleIsTheLastPicks = !title || (!!form.audioCredit && form.audioCredit.startsWith(`"${title}"`));

  return {
    audioUrl: pick.url,
    audioUploadedFileId: pick.uploadedFileId ?? null,
    ...(sameTrackNoCredit
      ? {}
      : {
          audioCredit: credit?.text ?? '',
          audioCreditUrl: credit?.url ?? '',
          audioCreditLicenseUrl: credit?.licenseUrl ?? '',
          ...(titleIsTheLastPicks ? { audioTitle: credit?.title ?? '' } : {}),
        }),
  };
};

export const emptyBlockForm = (category: BlockCategory, textKind: TextBlockKind = 'richtext'): BlockForm => ({
  category,
  access: PUBLIC_ACCESS_RULE,
  textKind,
  textSource: null,
  title: '',
  subtitle: '',
  content: '',
  images: [],
  layout: 'single',
  galleryStyle: 'masonry',
  effect: 'slide',
  allowFullscreen: true,
  autoplay: false,
  autoplaySeconds: 5,
  embedUrl: '',
  embedHtml: '',
  aspectRatio: '16:9',
  embedHeight: 360,
  maxWidth: null,
  weatherMode: 'current',
  weatherDays: 5,
  weatherUnits: 'metric',
  mapSource: 'event',
  mapSlotId: null,
  mapZoom: 14,
  showAddress: true,
  showContact: false,
  showWebsite: false,
  countdownTarget: 'start',
  countdownDoneText: '',
  scheduleShowVenue: true,
  scheduleShowDescription: true,
  // Speakers show by default (as before the setting existed); what more is said about them is the host's opt-in.
  scheduleShowSpeakers: true,
  scheduleShowSpeakerBio: false,
  scheduleShowSpeakerWebsite: false,
  scheduleShowSpeakerSocial: false,
  scheduleTrackMode: 'all',
  scheduleTracks: [],
  guestRoles: [],
  guestIds: [],
  guestGroupIds: [],
  guestRsvpStatuses: [],
  guestShowTitle: false,
  guestLayout: 'cards',
  guestShowRsvp: false,
  guestShowBio: false,
  guestShowWebsite: false,
  guestShowSocial: false,
  guestColumns: 4,
  guestAvatarSize: 'md',
  guestShowRole: true,
  stats: [{ value: '', label: '' }],
  statsColumns: 3,
  videoUrl: '',
  videoUploadedFileId: null,
  videoAutoplay: false,
  videoMuted: true,
  videoLoop: false,
  audioUrl: '',
  audioUploadedFileId: null,
  audioBackgroundUrl: '',
  audioBackgroundUploadedFileId: null,
  audioVolume: 80,
  audioBackgroundVolume: 40,
  audioAutoplay: false,
  audioLoop: true,
  audioTitle: '',
  audioVisualizer: 'bars',
  audioCredit: '',
  audioCreditUrl: '',
  audioCreditLicenseUrl: '',
  audioBackgroundCredit: '',
  audioBackgroundCreditUrl: '',
  audioBackgroundCreditLicenseUrl: '',
  dividerStyle: 'line',
  dividerIcon: null,
  dividerHeight: 32,
  highlightLayout: 'chips',
  ticketLayout: 'cards',
  ticketShowAddons: true,
  ticketShowDescription: true,
  ticketShowAvailability: true,
  ticketShowMembership: true,
  quoteText: '',
  quoteAuthor: '',
  quoteDate: '',
  quoteSource: '',
  quoteStyle: 'card',
  shareIntro: '',
  shareMessage: '',
  shareNetworks: SHARE_NETWORK_IDS,
  shareShowQr: true,
  shareShowCopy: true,
  contactAudience: 'anyone',
  contactIntro: '',
  lostFoundIntro: '',
  contactCollectPhone: true,
  contactButtonLabel: '',
  contactNotifyCrew: true,
  guestbookIntro: '',
  guestbookCanPost: true,
  guestbookAudience: 'all',
  guestbookRsvpStatuses: [],
  guestbookGroupIds: [],
  commentsAllow: false,
  commentsAudience: 'all',
  commentsRsvpStatuses: [],
  commentsGroupIds: [],
  commentsDmMode: 'off',
  cards: [makeCard()],
  cardColumns: 3,
  poiSource: 'all',
  poiCategoryId: null,
  poiLayout: 'cards',
  poiColumns: 2,
  poiShowPrice: true,
  poiShowContact: true,
  poiShowAddress: true,
  poiAllowSignup: false,
  poiButtonLabel: '',
  poiUntil: '',
  poiShowOthers: false,
  poiAllowUpvote: false,
  poiAllowComments: false,
  poiAllowPrintBlank: false,
  poiAllowPrintMine: false,
  accommodationSource: 'all',
  accommodationCategoryId: null,
  accommodationLayout: 'cards',
  accommodationColumns: 2,
  accommodationShowPrice: true,
  accommodationShowContact: true,
  accommodationShowAddress: true,
  accommodationShowDistance: true,
  accommodationShowBookingCode: true,
  accommodationShowRoster: false,
  accommodationAllowSignup: false,
  accommodationButtonLabel: '',
  accommodationUntil: '',
  accommodationAllowUpvote: false,
  accommodationAllowComments: false,
  accommodationAllowPrintBlank: false,
  accommodationAllowPrintMine: false,
  signupSource: 'all',
  signupCategoryId: null,
  signupLayout: 'cards',
  signupColumns: 2,
  signupShowPrice: true,
  signupShowQuantity: true,
  signupShowDescription: true,
  signupAllowSignup: false,
  signupUntil: '',
  signupButtonLabel: '',
  signupAllowUpvote: false,
  signupAllowComments: false,
  signupShowOthers: true,
  signupCollectPayments: false,
  signupAllowPrintBlank: false,
  signupAllowPrintMine: false,
  pollSource: 'all',
  pollCategoryId: null,
  pollLayout: 'list',
  pollColumns: 1,
  pollShowDescription: true,
  pollShowOptions: true,
  pollShowCorrect: false,
  pollAllowAnswer: false,
  pollButtonLabel: '',
  pollUntil: '',
  pollShowResults: false,
  pollResultsAfterAnswer: false,
  taskSource: 'all',
  taskCategoryId: null,
  taskType: 'all',
  taskLayout: 'cards',
  taskColumns: 2,
  taskShowSchedule: true,
  taskShowResponsible: true,
  taskShowProgress: true,
  taskAllowExecute: false,
  travelSource: 'all',
  travelSegmentIds: [],
  // Traveller names are OFF by default — this is a public page, and who is
  // flying where is personal. The host opts in per block.
  travelShowTravellers: false,
  travelShowStatus: true,
  travelShowCarrier: true,
  travelAllowShare: false,
  travelButtonLabel: '',
  travelUntil: '',
  travelShowOthers: false,
  budgetSource: 'all',
  budgetAllowContribute: false,
  budgetButtonLabel: '',
  budgetUntil: '',
  budgetAllowAddItem: false,
  budgetCategoryIds: [],
  budgetShowPaid: false,
  eventMapSource: 'all',
  eventMapCategoryId: null,
  mapWidgetHeight: 460,
  mapShowSchedule: true,
  partnerPackageSource: 'all',
  partnerPackageKind: null,
  partnerPackageIds: [],
  partnerPackageGroupByKind: true,
  partnerPackageShowPrice: true,
  partnerPackageShowSlots: true,
  partnerPackageButtonLabel: '',
  partnerPackageColumns: 3,
  volunteerSource: 'all',
  volunteerRoleIds: [],
  volunteerGroupByRole: true,
  volunteerShowRewards: true,
  volunteerShowSpots: true,
  volunteerButtonLabel: '',
  partnerSource: 'all',
  partnerKind: null,
  partnerPackageId: null,
  partnerIds: [],
  partnerLayout: 'grid',
  partnerColumns: 4,
  raceResultsSource: 'all',
  raceResultsGroupId: null,
  raceResultsLimit: null,
  raceResultsShowWave: true,
  partnerShowLogo: true,
  partnerShowName: true,
  partnerShowTier: false,
  partnerShowWebsite: true,
  partnerShowSocials: false,
  artistSource: 'all',
  artistBilling: null,
  artistIds: [],
  artistLayout: 'grid',
  artistColumns: 3,
  artistShowPhoto: true,
  artistShowBlurb: true,
  artistShowStage: true,
  artistShowSetTime: true,
  artistShowLinks: true,
  artistShowBilling: true,
  artistDates: 'next',
  prizeWinnerSource: 'all',
  prizeDrawIds: [],
  prizeWinnerShowPrize: true,
  prizeWinnerShowPending: true,
  prizeWinnerPendingText: '',
  organizerEventsMode: 'all',
  organizerEventsLimit: ORGANIZER_EVENTS_DEFAULT_LIMIT,
  organizerEventsLayout: 'cards',
  organizerFooterShowWebsite: true,
  organizerFooterShowSocial: true,
  organizerFooterShowEmail: true,
  organizerFooterShowPhone: true,
  organizerFooterShowAddress: true,
  donateCauseId: null,
  donateAllowGifts: true,
  donateShowGoal: true,
  donateShowDonors: false,
  donateShowDonorNames: true,
  donateShowDonorAmounts: true,
  donateShowDonorComments: false,
  gameScoreboardGameId: '',
  gameScoreboardShowTeams: true,
  gameScoreboardShowEliminations: true,
  gameScoreboardShowPairings: false,
  myRoleGameId: '',
  links: [{ label: '', url: '', variant: 'filled' }],
  buttonLayout: 'stack',
  ...BLOCK_STYLE_DEFAULTS,
  live: true,
});

/**
 * The style section's settings at their defaults — what a new block starts with and what "Reset style" puts back
 * (A2e #84). ⓘ Only the shared STYLE settings; a block's content and its own per-kind options are untouched.
 */
export const BLOCK_STYLE_DEFAULTS: Pick<BlockForm, 'backgroundColor' | 'textColor' | 'backgroundImage' | 'columns' | 'align' | 'fontKey' | 'textSize'> = {
  backgroundColor: null,
  textColor: null,
  backgroundImage: null,
  columns: 1,
  align: 'left',
  fontKey: null,
  textSize: null,
};

/**
 * Does the block differ from the default style in a setting its style section SHOWS? (Whether "Reset style" has
 * anything to do.) ⚠ Only shown settings count: a stored `columns` on a block that has no Columns control would
 * otherwise enable Reset with nothing visible to reset.
 */
export const blockStyleIsCustomized = (form: BlockForm): boolean => {
  const caps = blockStyleCaps(form.category);
  const shown: (keyof typeof BLOCK_STYLE_DEFAULTS)[] = [
    'backgroundColor',
    'textColor',
    'backgroundImage',
    ...(caps.align ? (['align'] as const) : []),
    ...(caps.columns ? (['columns'] as const) : []),
    ...(caps.font ? (['fontKey', 'textSize'] as const) : []),
  ];

  return shown.some((k) => form[k] !== BLOCK_STYLE_DEFAULTS[k]);
};

/** The form with its style section back at the defaults. */
export const resetBlockStyle = (form: BlockForm): BlockForm => ({ ...form, ...BLOCK_STYLE_DEFAULTS });

/** The stats rows that will be saved (a row with a live source, or with any value or label). */
export const blockValidStats = (form: BlockForm) => form.stats.filter((s) => (s.source && s.source !== 'static') || s.value.trim() || s.label.trim());

/** The buttons that will be saved: a label AND a target of their kind (an address, a page, a block or an action — A2e #85). */
export const blockValidLinks = (form: BlockForm) => form.links.filter((l) => l.label.trim() && linkHasTarget(l));

/** Does a button row hold anything at all? (An untouched row is ignored; a half-filled one is a problem to show.) */
export const linkRowStarted = (l: BlockForm['links'][number]): boolean => !!l.label.trim() || linkHasTarget(l);

/**
 * A2e #85 — what each button row is MISSING, keyed by row index, for the message AT that row's control (a half-filled
 * row used to vanish on save without a word). `known` (when the editor has read them) also flags a target that no
 * longer exists: a deleted page, a removed block — which the live site would silently hide.
 * ⓘ Presence only, as the house rule asks: the label, and a target of the row's own kind.
 */
export interface LinkRowProblem {
  field: 'label' | 'target';
  /** `missing` — not filled in yet (shown once Save was tried); `gone` — its page / block no longer exists (shown at once). */
  reason: 'missing' | 'gone';
  /** A stable key for the translation (`events:website.linkProblem.<code>`); `message` is its English default. */
  code: 'enterUrl' | 'choosePage' | 'chooseBlock' | 'chooseAction' | 'pageGone' | 'blockGone' | 'label';
  message: string;
}

export const linkRowProblems = (form: BlockForm, known: { pageIds?: string[]; blockIds?: string[] } = {}): Record<number, LinkRowProblem> => {
  const problems: Record<number, LinkRowProblem> = {};

  form.links.forEach((l, i) => {
    if (!linkRowStarted(l)) {
      return;
    }

    const kind = linkKindOf(l);

    if (!linkHasTarget(l)) {
      problems[i] =
        kind === 'page'
          ? { field: 'target', reason: 'missing', code: 'choosePage', message: 'Choose a page' }
          : kind === 'block'
            ? { field: 'target', reason: 'missing', code: 'chooseBlock', message: 'Choose a block' }
            : kind === 'action'
              ? { field: 'target', reason: 'missing', code: 'chooseAction', message: 'Choose an action' }
              : { field: 'target', reason: 'missing', code: 'enterUrl', message: 'Enter the web address' };

      return;
    }

    if (kind === 'page' && known.pageIds && !known.pageIds.includes(l.pageId as string)) {
      problems[i] = { field: 'target', reason: 'gone', code: 'pageGone', message: 'That page no longer exists — choose another' };

      return;
    }

    if (kind === 'block' && known.blockIds && !known.blockIds.includes(l.blockId as string)) {
      problems[i] = { field: 'target', reason: 'gone', code: 'blockGone', message: 'That block is no longer on this page — choose another' };

      return;
    }

    if (!l.label.trim()) {
      problems[i] = { field: 'label', reason: 'missing', code: 'label', message: 'Give the button a label' };
    }
  });

  return problems;
};

/** The cards that will be saved (a title or a description). */
export const blockValidCards = (form: BlockForm) => form.cards.filter((c) => c.title.trim() || (c.description ?? '').trim());

/**
 * The widget a block form saves as — config, stored widgetType, title, content and status. The editor sends it as is
 * (plus the page and position on create); the wizard does the same with an `emptyBlockForm`.
 */
/**
 * A people block's saved config — the guests block and the hosts & organizers block alike (owner, 2026-10-21): the
 * combined filter (`guestSource: 'filter'`, read by `peopleFilterOf` on the site and in the preview) and how people show.
 * ⓘ `hostRoles` is still written for the hosts block, and the old single `guestGroupId` cleared, so nothing reads a stale
 *   value of the older shape.
 */
const peopleBlockConfig = (form: BlockForm, kind: 'guests' | 'hosts'): Record<string, unknown> => ({
  guestKind: kind,
  guestSource: 'filter',
  guestRoles: form.guestRoles,
  guestIds: form.guestIds,
  guestGroupIds: form.guestGroupIds,
  guestRsvpStatuses: form.guestRsvpStatuses,
  guestGroupId: null,
  ...(kind === 'hosts' ? { hostRoles: form.guestRoles } : {}),
  guestShowRole: form.guestShowRole,
  guestShowRsvp: form.guestShowRsvp,
  guestShowTitle: form.guestShowTitle,
  guestLayout: form.guestLayout,
  guestShowBio: form.guestShowBio,
  guestShowWebsite: form.guestShowWebsite,
  guestShowSocial: form.guestShowSocial,
  guestColumns: form.guestColumns,
  guestAvatarSize: form.guestAvatarSize,
});

export const buildBlockWidgetInput = (form: BlockForm): PageWidgetInput => {
  const isText = form.category === 'text';
  const isImage = form.category === 'image';
  const isEmbed = form.category === 'embed';
  const isWeather = form.category === 'weather';
  const isMap = form.category === 'map';
  const isCountdown = form.category === 'countdown';
  const isSchedule = form.category === 'schedule';
  const isGuests = form.category === 'guests';
  const isHosts = form.category === 'hosts';
  const isHighlights = form.category === 'highlights';
  const isStats = form.category === 'stats';
  const isTickets = form.category === 'tickets';
  const isQuote = form.category === 'quote';
  const isCards = form.category === 'cards';
  const isPois = form.category === 'pois';
  const isAccommodations = form.category === 'accommodations';
  const isSignup = form.category === 'signup';
  const isPoll = form.category === 'poll';
  const isTasks = form.category === 'tasks';
  const isTravel = form.category === 'travel';
  const isBudget = form.category === 'budget';
  const isEventMap = form.category === 'eventMap';
  const isPartners = form.category === 'partners';
  const isPartnerPackages = form.category === 'partnerPackages';
  const isVolunteerShifts = form.category === 'volunteerShifts';
  const isArtists = form.category === 'artists';
  const isPrizeWinners = form.category === 'prizeWinners';
  const isOrganizerEvents = form.category === 'organizerEvents';
  const isOrganizerFooter = form.category === 'organizerFooter';
  const isDonate = form.category === 'donate';
  const isGameScoreboard = form.category === 'gameScoreboard';
  const isMyRole = form.category === 'myRole';
  const isRaceResults = form.category === 'raceResults';
  const isShare = form.category === 'share';
  const isAudio = form.category === 'audio';
  const isContact = form.category === 'contactUs';
  const isLostFound = form.category === 'lostFound';
  const isGuestbook = form.category === 'guestbook';
  const isVideo = form.category === 'video';
  const isDivider = form.category === 'divider';
  const isLinks = form.category === 'links';

  const validStats = blockValidStats(form);
  const validLinks = blockValidLinks(form);
  const validCards = blockValidCards(form);

  const config: WidgetConfig = {
    subtitle: form.subtitle.trim() || null,
    backgroundColor: form.backgroundColor,
    textColor: form.textColor,
    // A2e #88 — only when set: a block without one stores nothing new (and a saved config without it removes it).
    ...(form.backgroundImage ? { backgroundImage: form.backgroundImage } : {}),
    columns: form.columns,
    align: form.align,
    // A2e #84 — keys only, and only when set (a default block stores nothing new).
    ...(form.fontKey ? { fontKey: form.fontKey } : {}),
    ...(form.textSize && form.textSize !== 'md' ? { textSize: form.textSize } : {}),
  };
  if (isText) {
    // ⚠ A LIVE block is plain text (the Overview is), and carries no content of its own — the server fills it.
    config.textKind = form.textSource ? 'text' : form.textKind;
    config.textSource = form.textSource ?? null;
  } else if (isImage) {
    config.images = form.images.map((img) => ({ uploadedFileId: img.uploadedFileId ?? null, url: img.url, caption: img.caption?.trim() || null }));
    config.layout = form.layout;
    config.galleryStyle = form.galleryStyle;
    config.effect = form.effect;
    config.allowFullscreen = form.allowFullscreen;
    config.autoplay = form.autoplay;
    config.autoplaySeconds = form.autoplaySeconds;
  } else if (isEmbed) {
    config.embedUrl = form.embedUrl.trim() || null;
    config.embedHtml = form.embedHtml.trim() || null;
    config.aspectRatio = form.aspectRatio;
    config.embedHeight = form.aspectRatio === 'auto' ? form.embedHeight : null;
    config.maxWidth = form.maxWidth && form.maxWidth > 0 ? form.maxWidth : null;
  } else if (isWeather) {
    config.weatherMode = form.weatherMode;
    config.weatherDays = form.weatherDays;
    config.weatherUnits = form.weatherUnits;
  } else if (isMap) {
    config.mapSource = form.mapSource;
    config.mapSlotId = form.mapSource === 'slot' ? form.mapSlotId : null;
    config.mapZoom = form.mapZoom;
    config.showAddress = form.showAddress;
    config.showContact = form.showContact;
    config.showWebsite = form.showWebsite;
  } else if (isCountdown) {
    config.countdownTarget = form.countdownTarget;
    config.countdownDoneText = form.countdownDoneText.trim() || null;
  } else if (isSchedule) {
    config.scheduleShowVenue = form.scheduleShowVenue;
    config.scheduleShowDescription = form.scheduleShowDescription;
    config.scheduleShowSpeakers = form.scheduleShowSpeakers;
    config.scheduleShowSpeakerBio = form.scheduleShowSpeakerBio;
    config.scheduleShowSpeakerWebsite = form.scheduleShowSpeakerWebsite;
    config.scheduleShowSpeakerSocial = form.scheduleShowSpeakerSocial;
    config.scheduleTrackMode = form.scheduleTrackMode;
    // Only meaningful for 'selected' — an 'all' block keeps no stale list that would come back if switched again.
    config.scheduleTracks = form.scheduleTrackMode === 'selected' ? form.scheduleTracks : [];
  } else if (isGuests) {
    Object.assign(config, peopleBlockConfig(form, 'guests'));
  } else if (isHosts) {
    Object.assign(config, peopleBlockConfig(form, 'hosts'));
  } else if (isHighlights) {
    config.customKind = 'highlights';
    config.highlightLayout = form.highlightLayout;
  } else if (isTickets) {
    config.customKind = 'tickets';
    config.ticketLayout = form.ticketLayout;
    config.ticketShowAddons = form.ticketShowAddons;
    config.ticketShowDescription = form.ticketShowDescription;
    config.ticketShowAvailability = form.ticketShowAvailability;
    config.ticketShowMembership = form.ticketShowMembership;
  } else if (isQuote) {
    config.customKind = 'quote';
    config.quoteText = form.quoteText.trim();
    config.quoteAuthor = form.quoteAuthor.trim() || null;
    config.quoteDate = form.quoteDate.trim() || null;
    config.quoteSource = form.quoteSource.trim() || null;
    config.quoteStyle = form.quoteStyle;
  } else if (isAudio) {
    config.customKind = 'audio';
    config.audioUrl = form.audioUrl.trim() || null;
    config.audioUploadedFileId = form.audioUploadedFileId || null;
    // ⓘ A background with no url is no background — its stored id goes with it.
    config.audioBackgroundUrl = form.audioBackgroundUrl.trim() || null;
    config.audioBackgroundUploadedFileId = form.audioBackgroundUrl.trim() ? form.audioBackgroundUploadedFileId || null : null;
    config.audioVolume = form.audioVolume;
    config.audioBackgroundVolume = form.audioBackgroundVolume;
    config.audioAutoplay = form.audioAutoplay;
    config.audioLoop = form.audioLoop;
    config.audioTitle = form.audioTitle.trim() || null;
    config.audioVisualizer = form.audioVisualizer;
    config.audioCredit = form.audioCredit.trim() || null;
    config.audioCreditUrl = form.audioCredit.trim() ? form.audioCreditUrl.trim() || null : null;
    config.audioCreditLicenseUrl = form.audioCredit.trim() ? form.audioCreditLicenseUrl.trim() || null : null;
    // A credit belongs to its track: no background, no background credit.
    config.audioBackgroundCredit = form.audioBackgroundUrl.trim() ? form.audioBackgroundCredit.trim() || null : null;
    config.audioBackgroundCreditUrl = config.audioBackgroundCredit ? form.audioBackgroundCreditUrl.trim() || null : null;
    config.audioBackgroundCreditLicenseUrl = config.audioBackgroundCredit ? form.audioBackgroundCreditLicenseUrl.trim() || null : null;
  } else if (isShare) {
    config.customKind = 'share';
    config.shareIntro = form.shareIntro.trim() || null;
    config.shareMessage = form.shareMessage.trim() || null;
    // ⚠ WRITTEN EVEN WHEN EMPTY. `[]` is the host's choice of "no networks"; omitting
    // the key would read back as "never configured" and restore all seven.
    config.shareNetworks = form.shareNetworks;
    config.shareShowQr = form.shareShowQr;
    config.shareShowCopy = form.shareShowCopy;
  } else if (isContact) {
    config.customKind = 'contactUs';
    config.contactAudience = form.contactAudience;
    config.contactIntro = form.contactIntro.trim() || null;
    config.contactCollectPhone = form.contactCollectPhone;
    config.contactButtonLabel = form.contactButtonLabel.trim() || null;
    config.contactNotifyCrew = form.contactNotifyCrew;
  } else if (isLostFound) {
    // #68 — who may use it is the event's lost & found settings (the Lost & found page), not the block's.
    config.customKind = 'lostFound';
    config.lostFoundIntro = form.lostFoundIntro.trim() || null;
  } else if (isGuestbook) {
    config.customKind = 'guestbook';
    config.guestbookIntro = form.guestbookIntro.trim() || null;
    // Posting/audience now come from the generic config.comments (written centrally below).
  } else if (isCards) {
    config.customKind = 'cards';
    config.cards = validCards.map((c) => ({
      title: c.title.trim(),
      description: (c.description ?? '').trim() || null,
      url: (c.url ?? '').trim() || null,
      icon: c.icon || null,
      bgColor: c.bgColor || null,
      textColor: c.textColor || null,
    }));
    config.cardColumns = form.cardColumns;
  } else if (isPois) {
    config.customKind = 'pois';
    config.poiSource = form.poiSource;
    config.poiCategoryId = form.poiSource === 'category' ? form.poiCategoryId : null;
    config.poiLayout = form.poiLayout;
    config.poiColumns = form.poiColumns;
    config.poiShowPrice = form.poiShowPrice;
    config.poiShowContact = form.poiShowContact;
    config.poiShowAddress = form.poiShowAddress;
    config.poiAllowSignup = form.poiAllowSignup;
    config.poiButtonLabel = form.poiButtonLabel.trim() || null;
    config.poiUntil = form.poiUntil.trim() || null;
    config.poiShowOthers = form.poiShowOthers;
    config.poiAllowUpvote = form.poiAllowUpvote;
    config.poiAllowPrintBlank = form.poiAllowPrintBlank;
    config.poiAllowPrintMine = form.poiAllowPrintMine;
  } else if (isAccommodations) {
    config.customKind = 'accommodations';
    config.accommodationSource = form.accommodationSource;
    config.accommodationCategoryId = form.accommodationSource === 'category' ? form.accommodationCategoryId : null;
    config.accommodationLayout = form.accommodationLayout;
    config.accommodationColumns = form.accommodationColumns;
    config.accommodationShowPrice = form.accommodationShowPrice;
    config.accommodationShowContact = form.accommodationShowContact;
    config.accommodationShowAddress = form.accommodationShowAddress;
    config.accommodationShowDistance = form.accommodationShowDistance;
    config.accommodationShowBookingCode = form.accommodationShowBookingCode;
    config.accommodationShowRoster = form.accommodationShowRoster;
    config.accommodationAllowSignup = form.accommodationAllowSignup;
    config.accommodationButtonLabel = form.accommodationButtonLabel.trim() || null;
    config.accommodationUntil = form.accommodationUntil.trim() || null;
    config.accommodationAllowUpvote = form.accommodationAllowUpvote;
    config.accommodationAllowPrintBlank = form.accommodationAllowPrintBlank;
    config.accommodationAllowPrintMine = form.accommodationAllowPrintMine;
  } else if (isSignup) {
    config.customKind = 'signup';
    config.signupSource = form.signupSource;
    config.signupCategoryId = form.signupSource === 'category' ? form.signupCategoryId : null;
    config.signupLayout = form.signupLayout;
    config.signupColumns = form.signupColumns;
    config.signupShowPrice = form.signupShowPrice;
    config.signupShowQuantity = form.signupShowQuantity;
    config.signupShowDescription = form.signupShowDescription;
    config.signupAllowSignup = form.signupAllowSignup;
    config.signupUntil = form.signupUntil.trim() || null;
    config.signupButtonLabel = form.signupButtonLabel.trim() || null;
    config.signupAllowUpvote = form.signupAllowUpvote;
    config.signupShowOthers = form.signupShowOthers;
    config.signupCollectPayments = form.signupCollectPayments;
    config.signupAllowPrintBlank = form.signupAllowPrintBlank;
    config.signupAllowPrintMine = form.signupAllowPrintMine;
  } else if (isPoll) {
    config.customKind = 'poll';
    config.pollSource = form.pollSource;
    config.pollCategoryId = form.pollSource === 'category' ? form.pollCategoryId : null;
    config.pollLayout = form.pollLayout;
    config.pollColumns = form.pollColumns;
    config.pollShowDescription = form.pollShowDescription;
    config.pollShowOptions = form.pollShowOptions;
    config.pollShowCorrect = form.pollShowCorrect;
    config.pollAllowAnswer = form.pollAllowAnswer;
    config.pollButtonLabel = form.pollButtonLabel.trim() || null;
    config.pollUntil = form.pollUntil.trim() || null;
    config.pollShowResults = form.pollShowResults;
    // "After they answer" needs guests to answer HERE — else this block's counts would never show.
    config.pollResultsAfterAnswer = form.pollShowResults && form.pollAllowAnswer && form.pollResultsAfterAnswer;
  } else if (isTasks) {
    config.customKind = 'tasks';
    config.taskSource = form.taskSource;
    config.taskCategoryId = form.taskSource === 'category' ? form.taskCategoryId : null;
    config.taskType = form.taskType === 'all' ? null : form.taskType;
    config.taskLayout = form.taskLayout;
    config.taskColumns = form.taskColumns;
    config.taskShowSchedule = form.taskShowSchedule;
    config.taskShowResponsible = form.taskShowResponsible;
    config.taskShowProgress = form.taskShowProgress;
    config.taskAllowExecute = form.taskAllowExecute;
  } else if (isTravel) {
    config.customKind = 'travel';
    config.travelSource = form.travelSource;
    config.travelSegmentIds = form.travelSource === 'selected' ? form.travelSegmentIds : [];
    config.travelShowTravellers = form.travelShowTravellers;
    config.travelShowStatus = form.travelShowStatus;
    config.travelShowCarrier = form.travelShowCarrier;
    config.travelAllowShare = form.travelAllowShare;
    config.travelButtonLabel = form.travelButtonLabel.trim() || null;
    config.travelUntil = form.travelUntil.trim() || null;
    config.travelShowOthers = form.travelShowOthers;
  } else if (isBudget) {
    config.customKind = 'budget';
    config.budgetSource = form.budgetSource;
    config.budgetCategoryIds = form.budgetSource === 'categories' ? form.budgetCategoryIds : [];
    config.budgetShowPaid = form.budgetShowPaid;
    config.budgetAllowContribute = form.budgetAllowContribute;
    config.budgetButtonLabel = form.budgetButtonLabel.trim() || null;
    config.budgetUntil = form.budgetUntil.trim() || null;
    config.budgetAllowAddItem = form.budgetAllowAddItem;
  } else if (isEventMap) {
    config.customKind = 'eventMap';
    config.eventMapSource = form.eventMapSource;
    config.eventMapCategoryId = form.eventMapSource === 'category' ? form.eventMapCategoryId : null;
    config.mapWidgetHeight = form.mapWidgetHeight;
    config.mapShowSchedule = form.mapShowSchedule;
  } else if (isRaceResults) {
    config.customKind = 'raceResults';
    config.raceResultsSource = form.raceResultsSource;
    config.raceResultsGroupId = form.raceResultsSource === 'group' ? form.raceResultsGroupId : null;
    config.raceResultsLimit = form.raceResultsLimit || null;
    config.raceResultsShowWave = form.raceResultsShowWave;
  } else if (isPartnerPackages) {
    config.customKind = 'partnerPackages';
    config.partnerPackageSource = form.partnerPackageSource;
    // ⚠ The narrowing keys are CLEARED when they no longer apply. Left behind, switching back to
    // "one type" later would silently re-apply a kind the host chose weeks ago.
    config.partnerPackageKind = form.partnerPackageSource === 'kind' ? form.partnerPackageKind : null;
    config.partnerPackageIds = form.partnerPackageSource === 'selected' ? form.partnerPackageIds : [];
    config.partnerPackageGroupByKind = form.partnerPackageGroupByKind;
    config.partnerPackageShowPrice = form.partnerPackageShowPrice;
    config.partnerPackageShowSlots = form.partnerPackageShowSlots;
    config.partnerPackageButtonLabel = form.partnerPackageButtonLabel.trim() || undefined;
    config.partnerPackageColumns = form.partnerPackageColumns;
  } else if (isVolunteerShifts) {
    config.customKind = 'volunteerShifts';
    config.volunteerSource = form.volunteerSource;
    // ⚠ Cleared when it no longer applies — left behind, switching back to "some roles" later would silently
    // re-apply a choice made weeks ago.
    config.volunteerRoleIds = form.volunteerSource === 'roles' ? form.volunteerRoleIds : [];
    config.volunteerGroupByRole = form.volunteerGroupByRole;
    config.volunteerShowRewards = form.volunteerShowRewards;
    config.volunteerShowSpots = form.volunteerShowSpots;
    config.volunteerButtonLabel = form.volunteerButtonLabel.trim() || undefined;
  } else if (isPartners) {
    config.customKind = 'partners';
    config.partnerSource = form.partnerSource;
    config.partnerKind = form.partnerSource === 'kind' ? form.partnerKind : null;
    config.partnerPackageId = form.partnerSource === 'package' ? form.partnerPackageId : null;
    config.partnerIds = form.partnerSource === 'selected' ? form.partnerIds : [];
    config.partnerLayout = form.partnerLayout;
    config.partnerColumns = form.partnerColumns;
    config.partnerShowLogo = form.partnerShowLogo;
    config.partnerShowName = form.partnerShowName;
    config.partnerShowTier = form.partnerShowTier;
    config.partnerShowWebsite = form.partnerShowWebsite;
    config.partnerShowSocials = form.partnerShowSocials;
  } else if (isArtists) {
    config.customKind = 'artists';
    config.artistSource = form.artistSource;
    config.artistBilling = form.artistSource === 'billing' ? form.artistBilling : null;
    config.artistIds = form.artistSource === 'picked' ? form.artistIds : [];
    config.artistLayout = form.artistLayout;
    config.artistColumns = form.artistColumns;
    config.artistShowPhoto = form.artistShowPhoto;
    config.artistShowBlurb = form.artistShowBlurb;
    config.artistShowStage = form.artistShowStage;
    config.artistShowSetTime = form.artistShowSetTime;
    config.artistShowLinks = form.artistShowLinks;
    config.artistShowBilling = form.artistShowBilling;
    config.artistDates = form.artistDates;
  } else if (isPrizeWinners) {
    config.customKind = 'prizeWinners';
    config.prizeWinnerSource = form.prizeWinnerSource;
    config.prizeDrawIds = form.prizeWinnerSource === 'picked' ? form.prizeDrawIds : [];
    config.prizeWinnerShowPrize = form.prizeWinnerShowPrize;
    config.prizeWinnerShowPending = form.prizeWinnerShowPending;
    config.prizeWinnerPendingText = form.prizeWinnerPendingText.trim() || null;
  } else if (isOrganizerEvents) {
    config.customKind = 'organizerEvents';
    config.organizerEventsMode = form.organizerEventsMode;
    // Empty = the block's default (resolve/organizer `organizerEventsLimitOf`).
    config.organizerEventsLimit = form.organizerEventsLimit ?? null;
    config.organizerEventsLayout = form.organizerEventsLayout;
  } else if (isOrganizerFooter) {
    config.customKind = 'organizerFooter';
    config.organizerFooterShowWebsite = form.organizerFooterShowWebsite;
    config.organizerFooterShowSocial = form.organizerFooterShowSocial;
    config.organizerFooterShowEmail = form.organizerFooterShowEmail;
    config.organizerFooterShowPhone = form.organizerFooterShowPhone;
    config.organizerFooterShowAddress = form.organizerFooterShowAddress;
  } else if (isDonate) {
    config.customKind = 'donate';
    // Empty = every cause of the event (resolve/causes `filterCausesForWidget`). A stored `goal` block becomes this on save.
    config.donateCauseId = form.donateCauseId || null;
    config.donateAllowGifts = form.donateAllowGifts;
    config.donateShowGoal = form.donateShowGoal;
    config.donateShowDonors = form.donateShowDonors;
    config.donateShowDonorNames = form.donateShowDonorNames;
    config.donateShowDonorAmounts = form.donateShowDonorAmounts;
    config.donateShowDonorComments = form.donateShowDonorComments;
  } else if (isGameScoreboard) {
    config.customKind = 'gameScoreboard';
    config.gameScoreboardGameId = form.gameScoreboardGameId || null;
    config.gameScoreboardShowTeams = form.gameScoreboardShowTeams;
    config.gameScoreboardShowEliminations = form.gameScoreboardShowEliminations;
    config.gameScoreboardShowPairings = form.gameScoreboardShowPairings;
  } else if (isMyRole) {
    config.customKind = 'myRole';
    config.myRoleGameId = form.myRoleGameId || null;
  } else if (isStats) {
    config.customKind = 'stats';
    config.stats = validStats.map((s) => ({ value: s.value.trim(), label: s.label.trim(), source: s.source ?? 'static' }));
    config.statsColumns = form.statsColumns;
  } else if (isVideo) {
    config.videoUrl = form.videoUrl.trim() || null;
    config.videoUploadedFileId = form.videoUploadedFileId || null;
    config.aspectRatio = form.aspectRatio;
    config.embedHeight = form.aspectRatio === 'auto' ? form.embedHeight : null;
    config.maxWidth = form.maxWidth && form.maxWidth > 0 ? form.maxWidth : null;
    config.videoAutoplay = form.videoAutoplay;
    config.videoMuted = form.videoMuted;
    config.videoLoop = form.videoLoop;
  } else if (isDivider) {
    config.customKind = 'divider';
    config.dividerStyle = form.dividerStyle;
    config.dividerIcon = form.dividerStyle === 'icon' ? form.dividerIcon || null : null;
    config.dividerHeight = form.dividerHeight;
  } else if (isLinks) {
    config.links = validLinks.map((l) => {
      const kind = linkKindOf(l);

      // A2e #85 — only the target of the button's OWN kind is kept (a page button switched back to a web address does
      // not carry its old page along). A web address stores no kind, exactly as every button did before.
      return {
        label: l.label.trim(),
        url: kind === 'url' ? l.url.trim() : '',
        ...(kind !== 'url' ? { kind } : {}),
        ...(kind === 'page' ? { pageId: l.pageId } : {}),
        ...(kind === 'block' ? { blockId: l.blockId } : {}),
        ...(kind === 'action' ? { action: l.action } : {}),
        icon: l.icon || null,
        variant: l.variant ?? 'filled',
        bgColor: l.bgColor || null,
        textColor: l.textColor || null,
      };
    });
    config.buttonLayout = form.buttonLayout;
  }

  // Generic comment settings (the SAME shape on every comment-supporting block) — written centrally so all
  // entity kinds behave identically. Supersedes the legacy per-kind fields (guestbook* / <kind>AllowComments).
  if (isGuestbook || isPois || isAccommodations || isSignup || isBudget || isTravel || isTasks) {
    config.comments = {
      allow: form.commentsAllow,
      audience: form.commentsAudience,
      rsvpStatuses: form.commentsAudience === 'rsvp_status' ? form.commentsRsvpStatuses : [],
      groupIds: form.commentsAudience === 'group' ? form.commentsGroupIds : [],
      // Direct messages are an event-wide feature, configured only on the guest book (the community hub).
      ...(isGuestbook ? { dm: { mode: form.commentsDmMode } } : {}),
    };
  }

  const widgetType: string = isText
    ? widgetTypeForText(form.textSource ? 'text' : form.textKind)
    : isEmbed
      ? 'embed'
      : isWeather
        ? 'weather'
        : isMap
          ? 'map'
          : isCountdown
            ? 'countdown'
            : isSchedule
              ? 'agenda'
              : isGuests || isHosts
                ? 'guest_list'
                : isVideo
                  ? 'video'
                  : isStats || isDivider || isHighlights || isTickets || isQuote || isCards || isPois || isAccommodations || isSignup || isPoll || isTasks || isTravel || isBudget || isEventMap || isPartners || isPartnerPackages || isVolunteerShifts || isArtists || isPrizeWinners || isOrganizerEvents || isOrganizerFooter || isDonate || isGameScoreboard || isMyRole || isRaceResults || isContact || isLostFound || isGuestbook || isShare || isAudio
                    ? 'custom'
                    : isLinks
                      ? 'links'
                      : widgetTypeForImage(form.layout);

  const input: PageWidgetInput = {
    widgetType,
    title: form.title.trim() || null,
    // ⓘ A live block KEEPS its own text (never served — the server fills the Overview in): switching the Overview off
    //   again gives the host back what they had written, instead of an empty editor (found in review).
    content: isText ? form.content : null,
    config,
    status: form.live ? STATUSES.ACTIVE : STATUSES.DRAFT,
    // The block's own visibility — responses AND groups, cleared unless it is "Some guests".
    ...accessRuleInput(form.access),
  };

  return input;
};

/**
 * A widget as the editor PREVIEWS it: a live Overview block shows the event's Overview, as the public site will (the
 * server fills it there). Every other widget is returned untouched.
 */
export const withLiveText = <W extends { content?: string | null; config?: { textSource?: string | null } | null }>(
  widget: W,
  eventSummary: string | null | undefined,
): W =>
  widget.config?.textSource === TEXT_SOURCE_EVENT_SUMMARY ? { ...widget, content: (eventSummary ?? '').trim() || null } : widget;
